<template>
    <TransitionGroup name="fade">
        <AddWelcomeBonus
            v-if="!typeSelected"
            v-model:bonusType="welcomeBonus.type"
            v-model:tieredBonusType="tieredBonuses[0].type"
        />
        <div
            v-else
            class="p-4 rounded-lg flex flex-col gap-4 w-full"
            :class="{
                'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700':
                    backgroundColor === 'white',
                'bg-slate-50 dark:bg-slate-700/50': backgroundColor === 'slate',
            }"
        >
            <header
                class="flex justify-between items-center"
                v-if="welcomeBonus.type !== 'tiered'"
            >
                <h3 class="text-base font-medium text-slate-800 dark:text-slate-100 text-start">
                    {{ getBonusTitle(welcomeBonus.type) }}
                </h3>

                <button
                    v-if="welcomeBonus.type !== 'tiered'"
                    @click="$emit('delete')"
                    type="button"
                    class="btn-base btn-transparent !p-0"
                >
                    <Icon icon="trash" class="w-5 h-5" />
                    Delete
                </button>
            </header>
            <TransitionGroup name="fade">
                <div
                    class="grid sm:grid-cols-2 grid-cols-1 auto-rows-auto gap-y-6 gap-x-2"
                    v-if="welcomeBonus.type !== 'tiered'"
                >
                    <!-- Bonus Amount -->
                    <!-- bonus points/nights input -->
                    <UiInput
                        v-if="
                            welcomeBonus.type === 'points' ||
                            welcomeBonus.type === 'free_nights'
                        "
                        id="bonus-amount"
                        name="bonus-amount"
                        :label="bonusAmountInputLabel"
                        :error="errors.bonus_amount"
                    >
                        <template #input>
                            <vue-number-format
                                v-model:value="welcomeBonus.bonus_amount"
                                :placeholder="bonusAmountInputLabel"
                                class="form-input w-full pr-9 disabled:cursor-not-allowed"
                                min="0"
                                :options="{
                                    decimal: '.',
                                    thousands: ',',
                                    precision: 0,
                                }"
                            />
                        </template>
                    </UiInput>

                    <!-- Certificate Type Select -->
                    <UiInput
                        v-if="welcomeBonus.type === 'free_nights'"
                        id="certificate-type"
                        name="certificate-type"
                        label="Certificate type"
                        :error="errors.certificate_type"
                    >
                        <template #input>
                            <select
                                id="certificate-type"
                                v-model="welcomeBonus.bonus_units"
                                class="form-select w-full active border disabled:cursor-not-allowed"
                            >
                                <option value="">
                                    Select certificate type
                                </option>
                                <option value="hilton">
                                    Hilton free nights
                                </option>
                                <option value="hyatt">Hyatt free nights</option>
                                <option value="ihg">IHG free nights</option>
                                <option value="marriott">
                                    Marriott free nights
                                </option>
                                <option value="other">Other free nights</option>
                            </select>
                        </template>
                    </UiInput>
                    <!-- bonus cash back input -->
                    <UiInput
                        v-if="welcomeBonus.type === 'cashback'"
                        id="bonus-amount"
                        name="bonus-amount"
                        label="Cash back amount"
                        :error="errors.bonus_amount"
                        :options="{
                            icon: 'dollar',
                            iconWrapperClass:
                                'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                        }"
                    >
                        <template #input>
                            <vue-number-format
                                v-model:value="welcomeBonus.bonus_amount"
                                :placeholder="'Cash back amount'"
                                class="form-input w-full active border pr-9 !pl-[54px] disabled:cursor-not-allowed"
                                min="0"
                                :options="{
                                    decimal: '.',
                                    thousands: ',',
                                    precision: 2,
                                }"
                            ></vue-number-format>
                        </template>
                    </UiInput>
                    <!-- Minimum Spend Amount -->
                    <UiInput
                        id="minimum_spend_amount"
                        name="minimum_spend_amount"
                        label="Minimum spend amount"
                        :error="errors.minimum_spend_amount"
                        :options="{
                            icon: 'dollar',
                            iconWrapperClass:
                                'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                        }"
                    >
                        <template #input>
                            <vue-number-format
                                v-model:value="welcomeBonus.minimum_spend_amount"
                                :placeholder="'Minimum spend amount'"
                                class="form-input w-full active border pr-9 !pl-[54px] disabled:cursor-not-allowed"
                                min="0"
                                :options="{
                                    decimal: '.',
                                    thousands: ',',
                                    decimals: 0,
                                    empty: '',
                                    raw: true,
                                }"
                            ></vue-number-format>
                        </template>
                    </UiInput>

                    <!-- Minimum Spend Deadline -->
                    <UiInput
                        class="w-full"
                        id="minimum_spend_deadline"
                        name="minimum_spend_deadline"
                        label="Minimum spend deadline"
                        :error="errors.minimum_spend_deadline"
                        :options="{
                            icon: 'calendar',
                        }"
                    >
                        <template #input>
                            <flat-pickr
                                id="minimum_spend_deadline"
                                v-model="welcomeBonus.minimum_spend_deadline"
                                type="date"
                                class="form-input date-picker flatpickr-input !pr-9 !pl-10 active border w-full disabled:cursor-not-allowed"
                                placeholder="Minimum spend deadline"
                                :config="minimumSpendDateConfig"
                            />
                        </template>
                    </UiInput>

                    <!-- Date Bonus Received -->
                    <div>
                        <UiInput
                            id="date_received"
                            name="date_received"
                            label="Date bonus received"
                            :disabled="!welcomeBonus.received"
                            :error="errors.date_received"
                            :options="{
                                icon: 'calendar',
                            }"
                        >
                            <template #input>
                                <flat-pickr
                                    id="date_received"
                                    v-model="welcomeBonus.date_received"
                                    type="date"
                                    class="form-input date-picker flatpickr-input !pr-9 !pl-10 active border w-full"
                                    :disabled="!welcomeBonus.received"
                                    :config="receivedDateConfig"
                                />
                            </template>
                        </UiInput>
                        <!-- Bonus Received Checkbox -->
                        <div class="mt-2" v-if="welcomeBonus.type !== 'tiered'">
                            <label
                                class="inline-flex items-center cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    v-model="welcomeBonus.received"
                                    class="form-checkbox"
                                />
                                <span class="ml-2 text-sm text-gray-700">
                                    Welcome bonus received
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </TransitionGroup>

            <!-- Tiered Bonus UI -->
            <Transition name="fade">
                <TransitionGroup
                    name="fade"
                    class="flex flex-col gap-6"
                    tag="div"
                    v-if="welcomeBonus.type === 'tiered'"
                >
                    <div
                        v-for="(tier, index) in tieredBonuses"
                        :key="index"
                        class="relative"
                    >
                        <div class="flex justify-between items-center">
                            <h4 class="font-medium text-slate-700 mb-4">
                                Bonus tier {{ index + 1 }}:
                                {{ getBonusTitle(tier.type) }}
                            </h4>
                            <button
                                @click="removeTier(index)"
                                type="button"
                                class="btn-base btn-transparent"
                                title="Remove tier"
                            >
                                <Icon icon="trash" class="w-4 h-4" />
                                Delete
                            </button>
                        </div>

                        <div
                            class="grid grid-cols-2 auto-rows-auto gap-y-6 gap-x-2"
                        >
                            <!-- Bonus Amount -->
                            <!-- bonus points/nights input -->
                            <UiInput
                                v-if="
                                    tier.type === 'points' ||
                                    tier.type === 'free_nights'
                                "
                                id="bonus-amount"
                                name="bonus-amount"
                                :label="getTierBonusLabel(tier.type)"
                                :error="errors[`tier_${index}_bonus_amount`]"
                            >
                                <template #input>
                                    <vue-number-format
                                        v-model:value="tier.bonus_amount"
                                        :placeholder="
                                            getTierBonusLabel(tier.type)
                                        "
                                        class="form-input w-full active border pr-9 disabled:cursor-not-allowed"
                                        min="0"
                                        :options="{
                                            decimal: '.',
                                            thousands: ',',
                                            precision:
                                                tier.type === 'cashback'
                                                    ? 2
                                                    : 0,
                                        }"
                                    />
                                </template>
                            </UiInput>

                            <!-- Certificate Type Select -->
                            <UiInput
                                v-if="tier.type === 'free_nights'"
                                id="certificate-type"
                                name="certificate-type"
                                label="Certificate type"
                                :error="errors.certificate_type"
                            >
                                <template #input>
                                    <select
                                        id="free_nights_bonus_units"
                                        v-model="tier.bonus_units"
                                        class="form-select w-full active border disabled:cursor-not-allowed"
                                    >
                                        <option value="">
                                            Select certificate type
                                        </option>
                                        <option value="hilton">
                                            Hilton free nights
                                        </option>
                                        <option value="hyatt">
                                            Hyatt free nights
                                        </option>
                                        <option value="ihg">
                                            IHG free nights
                                        </option>
                                        <option value="marriott">
                                            Marriott free nights
                                        </option>
                                        <option value="other">
                                            Other free nights
                                        </option>
                                    </select>
                                </template>
                            </UiInput>
                            <!-- bonus cash back input -->
                            <UiInput
                                v-if="tier.type === 'cashback'"
                                id="cashback_bonus_amount"
                                name="cashback_bonus_amount"
                                :label="getTierBonusLabel(tier.type)"
                                :error="errors[`tier_${index}_bonus_amount`]"
                                :options="{
                                    icon: 'dollar',
                                    iconWrapperClass:
                                        'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                                }"
                            >
                                <template #input>
                                    <vue-number-format
                                        v-model:value="tier.bonus_amount"
                                        :placeholder="
                                            getTierBonusLabel(tier.type)
                                        "
                                        class="form-input w-full active border pr-9 !pl-[54px] disabled:cursor-not-allowed"
                                        min="0"
                                        :options="{
                                            decimal: '.',
                                            thousands: ',',
                                            precision: 2,
                                        }"
                                    ></vue-number-format>
                                </template>
                            </UiInput>
                            <!-- Minimum Spend Amount -->
                            <UiInput
                                id="minimum_spend_amount"
                                name="minimum_spend_amount"
                                label="Minimum spend amount"
                                :error="
                                    errors[`tier_${index}_minimum_spend_amount`]
                                "
                                :options="{
                                    icon: 'dollar',
                                    iconWrapperClass:
                                        'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                                }"
                            >
                                <template #input>
                                    <vue-number-format
                                        v-model:value="
                                            tier.minimum_spend_amount
                                        "
                                        :placeholder="'Minimum spend amount'"
                                        class="form-input w-full active border pr-9 !pl-[54px] disabled:cursor-not-allowed"
                                        min="0"
                                        :options="{
                                            decimal: '.',
                                            thousands: ',',
                                            precision: 0,
                                        }"
                                    ></vue-number-format>
                                </template>
                            </UiInput>

                            <!-- Minimum Spend Deadline -->
                            <UiInput
                                id="minimum_spend_deadline"
                                name="minimum_spend_deadline"
                                label="Minimum spend deadline"
                                :error="
                                    errors[
                                        `tier_${index}_minimum_spend_deadline`
                                    ]
                                "
                                :options="{
                                    icon: 'calendar',
                                }"
                            >
                                <template #input>
                                    <flat-pickr
                                        id="minimum_spend_deadline"
                                        v-model="tier.minimum_spend_deadline"
                                        type="date"
                                        class="form-input date-picker flatpickr-input !pr-9 !pl-10 active border w-full disabled:cursor-not-allowed"
                                        placeholder="Minimum spend deadline"
                                        :config="minimumSpendDateConfig"
                                    />
                                </template>
                            </UiInput>
                            <div>
                                <!-- Date Bonus Received -->
                                <UiInput
                                    id="date_received"
                                    name="date_received"
                                    label="Date bonus received"
                                    :disabled="!tier.received"
                                    :error="
                                        errors[`tier_${index}_date_received`]
                                    "
                                    :options="{
                                        icon: 'calendar',
                                    }"
                                >
                                    <template #input>
                                        <flat-pickr
                                            id="date_received"
                                            v-model="tier.date_received"
                                            type="date"
                                            class="form-input date-picker flatpickr-input !pr-9 !pl-10 active border w-full disabled:cursor-not-allowed"
                                            :disabled="!tier.received"
                                            :config="receivedDateConfig"
                                        />
                                    </template>
                                </UiInput>
                                <div class="mt-2">
                                    <label
                                        class="inline-flex items-center cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            v-model="tier.received"
                                            class="form-checkbox"
                                        />
                                        <span
                                    class="ml-2 text-sm text-gray-700 dark:text-gray-200"
                                        >
                                            Bonus received
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Add Tier Button -->
                        <div
                            class="flex justify-center mt-4"
                            v-show="index === tieredBonuses.length - 1"
                        >
                            <button
                                type="button"
                                class="btn-base btn-hollow"
                                @click="toggleTierTypeSelector"
                                ref="buttonRef"
                            >
                                <span class="hidden sm:inline">Add Tier</span>
                                <Icon icon="plus" class="w-4 h-4" />
                            </button>
                            <TierTypeSelector
                                @select-tier-type="addNewTier"
                                @close="showTierSelector = false"
                                :show="showTierSelector"
                            />
                        </div>
                    </div>
                </TransitionGroup>
            </Transition>
        </div>
    </TransitionGroup>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import dayjs from 'dayjs';
import _ from 'lodash';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import AddWelcomeBonus from '@/components/wallet/partials/add-welcome-bonus.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import TierTypeSelector from '@/components/wallet/partials/tier-type-selector.vue';

const emit = defineEmits(['delete', 'update']);

const welcomeBonus = defineModel('welcome-bonus', {
    type: Object,
    default: () => ({}),
});

const tieredBonuses = defineModel('tiered-bonuses', {
    type: Array,
    default: () => [
        {
            tier_number: 1,
            bonus_amount: '',
            bonus_unit: '',
            minimum_spend_amount: '',
            minimum_spend_deadline: '',
            date_earned: '',
            received: false,
        },
    ],
});

const props = defineProps({
    errors: Object,
    backgroundColor: { type: String, default: 'slate' },
});

// Form state
const showTierSelector = ref(false);

const receivedDateConfig = {
    dateFormat: 'm/d/Y',
    maxDate: 'today',
    static: true,
    disableMobile: true,
};

// MY1-3050: match the inline mark-received flows — checking "received"
// defaults the date to today (still editable, still capped at today).
watch(
    () => welcomeBonus.value?.received,
    (received) => {
        if (received && !welcomeBonus.value.date_received) {
            welcomeBonus.value.date_received = dayjs().format('MM/DD/YYYY');
        }
    }
);

watch(
    () => (welcomeBonus.value?.tiers || []).map((tier) => tier.received),
    () => {
        (welcomeBonus.value?.tiers || []).forEach((tier) => {
            if (tier.received && !tier.date_received) {
                tier.date_received = dayjs().format('MM/DD/YYYY');
            }
        });
    }
);
const minimumSpendDateConfig = {
    dateFormat: 'm/d/Y',
    static: true,
    disableMobile: true,
};

// Computed properties
const typeSelected = computed(() => {
    const wb = welcomeBonus.value;
    // If type is set, use it
    if (wb?.type) {
        return wb.type;
    }
    // If welcome bonus has an id (exists in DB), infer type from bonus_units or default to 'points'
    if (wb?.id) {
        if (wb.bonus_units === 'cash_back' || wb.bonus_units === 'cashback') {
            return 'cash_back';
        }
        if (wb.bonus_units === 'free_nights' || wb.bonus_units === 'nights') {
            return 'free_nights';
        }
        // Default to points if we have an id but can't determine type
        return 'points';
    }
    return '';
});

const getBonusTitle = (bonusType) => {
    const titles = {
        points: 'Points bonus',
        cashback: 'Cash back bonus',
        tiered: 'Tiered bonus',
        free_nights: 'Free night certificates',
    };
    return titles[bonusType] || 'Welcome bonus';
};

const bonusAmountInputLabel = computed(() => {
    switch (welcomeBonus.value?.type) {
        case 'points':
            return 'Welcome bonus amount';
        case 'cashback':
            return 'Welcome bonus cash back';
        case 'tiered':
            return 'Welcome bonus tiered';
        case 'free_nights':
            return 'Number of certificates';
        default:
            return 'Welcome bonus amount';
    }
});

const toggleTierTypeSelector = () => {
    showTierSelector.value = !showTierSelector.value;
};

const addNewTier = (tierType) => {
    const nextTierNumber = tieredBonuses.value.length + 1;
    const updatedTiers = [
        ...tieredBonuses.value,
        {
            tier_number: nextTierNumber,
            bonus_amount: '',
            bonus_units: tierType,
            minimum_spend_amount: '',
            minimum_spend_deadline: '',
            date_received: '',
            received: false,
            type: tierType,
        },
    ];

    tieredBonuses.value = updatedTiers;
    showTierSelector.value = false;
};

const removeTier = (index) => {
    if (tieredBonuses.value.length === 1) {
        emit('delete');
        return;
    }
    const updatedTiers = [...tieredBonuses.value];
    updatedTiers.splice(index, 1);

    updatedTiers.forEach((tier, idx) => {
        tier.tier_number = idx + 1;
    });

    tieredBonuses.value = updatedTiers;
};

const getTierBonusLabel = (unitType) => {
    switch (unitType) {
        case 'points':
            return 'Points amount';
        case 'cashback':
            return 'Cash back amount';
        case 'free_nights':
            return 'Number of certificates';
        default:
            return 'Bonus amount';
    }
};
</script>

<style scoped>
:deep(.dp__main) {
    width: 100% !important;
}
</style>
